/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { apiToken } from './get-wine-api-token';
import { WineMapper } from './get-wine-mapper-class';

/**
 * GetWineCountries Input Action
 */

export class GetWineCountriesInput extends Msdyn365.CommerceEntityInput implements Msdyn365.IActionInput {

    private readonly _url: string = 'https://api.smwe.com/v1/SLR/countries';

    private readonly _token: string = apiToken;

    constructor() {
        super({ shouldCacheOutput: true, cacheObjectType: 'IGetWineCountriesData', cacheKey: 'SINGLE' });
    }

    public get url(): string { return this._url; }
    public get token(): string { return this._token; }
}

enum IGetWineCountriesMap {
    iso = 'iso',
    iso3 = 'iso3',
    name = 'name',
    printableName = 'printable_name',
    numCode = 'numcode'
}

/**
 * class definition for the output controller of the API
 */
class GetWineCountriesMapper extends WineMapper<IGetWineCountriesData[]> {
    public get output(): IGetWineCountriesDataOutput[] {
        return this._input.map(country => {
                return this._applyMap<
                    IGetWineCountriesDataOutput,
                    IGetWineCountriesData,
                    typeof IGetWineCountriesMap
                >(country, IGetWineCountriesMap);
            });
    }
}

export interface IGetWineCountriesData {
    iso: string;
    iso3: string;
    name: string;
    printable_name: string;
    numcode: number;
}

export interface IGetWineCountriesDataOutput {
    iso: string;
    iso3: string;
    name: string;
    printableName: string;
    numCode: number;
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetWineCountriesInput();
};

/**
 * action that retrieves the list of countries
 */
async function action(input: GetWineCountriesInput, ctx: Msdyn365.IActionContext): Promise<IGetWineCountriesDataOutput[]> {
    const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;

    const response = await Msdyn365.sendRequest<IGetWineCountriesData[]>(input.url, 'get', apiSettings, {
        headers: {
            Authorization: `Token token="${input.token}"`
        }
    });
    return new GetWineCountriesMapper(response.data).output;
}

export default Msdyn365.createDataAction({
    action: <Msdyn365.IAction<IGetWineCountriesDataOutput[]>>action,
    input: createInput
});
